import { PaginationState, SortingState } from '@tanstack/react-table';
import { z } from 'zod';
import { EmailAttachmentSchema } from './email-attachments';
import { SubmissionSchedulerRequest } from './submission-scheduler-request';
import { Filter } from './filter';

const emailListSchema = z.string().refine(
    (val) => {
        if (!val || val.trim() === '') return true; // Allow empty
        const emails = val.split(';').map(e => e.trim()).filter(e => e !== '');
        return emails.every(email => z.string().email().safeParse(email).success);
    },
    { message: 'One or more email addresses are invalid' }
);

export const EmailSchema = z.object({
    body: z.string(),
    ccList: emailListSchema,
    deliveryDateTime: z.preprocess(
        (val) => {
            // Convert empty string or invalid values to null
            if (!val || val === '' || (typeof val === 'string' && val.trim() === '')) {
                return null;
            }
            return val;
        },
        z.union([
            z.string().refine(
                (val) => {
                    // Check if it's a valid datetime string (can be parsed as Date)
                    const date = new Date(val);
                    return !isNaN(date.getTime());
                },
                { message: 'Invalid datetime format' }
            ),
            z.null(),
            z.undefined()
        ]).nullable().optional()
    ),
    emailAttachments: z.array(EmailAttachmentSchema).nullable().optional(),
    id: z.number(),
    isBCC: z.boolean(),
    recipient: z.string().email({ message: 'Invalid email address' }),
    sender: z.string().email({ message: 'Invalid email address' }),
    subject: z.string(),
    status: z.string(),
});

export type Email = z.infer<typeof EmailSchema>;
export const api: string = '/SubmissionScheduler';

export const addAttachments = async (
    emailId: number, 
    attachments: File[]
): Promise<boolean> => {
    try {
        
        const formData = new FormData();
        attachments.forEach((f: File) => formData.append('files', f));

        const response: Response = await fetch(`${api}/${emailId}/attachments`, {
            body: formData,           
            method: 'POST',
        });

        return response.ok;
    } catch (error) {
        console.error(error)
        return false;
    }
};

export const deleteAttachments = async (
    emailId: number,
    attachmentIds: number[]
): Promise<boolean> => {
    try {
        const response: Response = await fetch(`${api}/${emailId}/attachments`, {
            body: JSON.stringify(attachmentIds),
            headers: {
                'Content-Type': 'application/json'
            },
            method: 'DELETE',
        });

        return response.ok;
    } catch (error) {
        console.error(error);
        return false;
    }
};

export const deleteEmails = async (emailIds: number[]): Promise<boolean> => {
    try {
        
        const response: Response = await fetch(api, {
            body: JSON.stringify(emailIds),
            headers: {
                'Content-Type': 'application/json'
            },            
            method: 'DELETE',
        });

        return response.ok;
    } catch (error) {
        console.error(error)
        return false;
    }
};

export const get = async (): Promise<{ data: Email[]; total: number }> => {
    try {
        const response: Response = await fetch(api);
        return await response.json();
    } catch (error) {
        console.error(error);
		return {
			data: [],
			total: 0,
		};
    }
};

export const getById = async (id: number): Promise<Email | null> => {
    try {
        const response: Response = await fetch(`${api}/${id}`);
        return await response.json();        
    } catch (error) {
        console.error(error);
        return null;
    }
};

export const search = async (
    filter: Filter,
	pagination: PaginationState,
	sorting: SortingState = [],	
): Promise<{ data: Email[]; total: number }> => {
    try {

        const request: SubmissionSchedulerRequest = {
            filter: filter,
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
            sortBy: sorting[0]?.id || 'deliveryDateTime',
            sortDirection: sorting[0]?.desc ? 'desc' : 'asc'
        };      

        const response: Response = await fetch(api, {
            body: JSON.stringify(request),
            headers: {
                'Content-Type': 'application/json'
            },
            method: 'POST'
        });

        return await response.json();
    } catch (error) {
        console.error(error);
        return {
            data: [],
            total: 0
        }
    }
};

export const update = async (email: Email): Promise<boolean> => {
    try {
        const response: Response = await fetch(api, {            
            body: JSON.stringify(email),
            headers: {
                'Content-Type': 'application/json',
            },
            method: 'PATCH'
        });

        return response.ok;        
    } catch (error) {
        console.error(error);
        return false;
    }
};